import { Subscription } from '@rails/actioncable';
import {
  deleteV1CompaniesCompanyIdTicketsId,
  deleteV1CompaniesCompanyIdTicketsTicketIdImage,
  getV1CompaniesCompanyIdJobsJobIdTickets,
  getV1CompaniesCompanyIdTickets,
  GetV1CompaniesCompanyIdTicketsData,
  getV1CompaniesCompanyIdTicketsTicketIdAiMetadata,
  patchV1CompaniesCompanyIdTicketsId,
  postV1CompaniesCompanyIdTickets,
  PostV1CompaniesCompanyIdTicketsData,
  postV1CompaniesCompanyIdTicketsTicketIdImage,
  putV1CompaniesCompanyIdTicketsTicketIdApprove,
  putV1CompaniesCompanyIdTicketsTicketIdVoid,
  Ticket_Read,
  TicketStateEvent,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback, useState } from 'react';

import { useDirectUploads } from '~hooks/useDirectUploads/useDirectUploads';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';
import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store';
import { isUploadValidationError } from '~utils/extractAxiosError';
import { fetchAllApiPages } from '~utils/fetchAllPages';
import { subscribeToChannel } from '~utils/rtu/rtu-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { Ticket, TicketMetadata, TicketUpdateInput } from './models';

interface UpdateTicketByIdProps {
  companyId: string;
  id: string;
  data: TicketUpdateInput;
}

interface UpdateTicketPotentialDuplicateProps {
  companyId: string;
  id: string;
  potentialDuplicate: boolean;
}

interface DeleteByIdProps {
  companyId: string;
  id: string;
}

interface UploadTicketImageByIdProps {
  companyId: string;
  id: string;
  file: File;
}

interface CreateTicketProps {
  companyId: string;
  data: NonNullable<PostV1CompaniesCompanyIdTicketsData['body']>;
}

type GetTicketsQuery = NonNullable<GetV1CompaniesCompanyIdTicketsData['query']>;

export interface TicketFilterParams {
  from_date?: GetTicketsQuery['filter[from_date]'];
  missing_image?: GetTicketsQuery['filter[missing_image]'];
  missing_load?: GetTicketsQuery['filter[missing_load]'];
  states?: GetTicketsQuery['filter[states]'];
  to_date?: GetTicketsQuery['filter[to_date]'];
}

interface GetTicketsProps {
  companyId: string;
  cursor?: string;
  filterParams?: TicketFilterParams;
  limit: number;
  link?: PaginationLink;
  signal?: AbortSignal;
}

interface UpdateStateProps {
  companyId: string;
  event: TicketStateEvent;
  id: string;
}

interface GetAllTicketsByJobProps {
  companyId: string;
  jobId: string;
}

interface GetTicketMetadataByIdProps {
  companyId: string;
  id: string;
}

export const useTickets = () => {
  const { ticketsStore, userStore } = useStores();
  const [ticketSubscription, setTicketSubscription] = useState<Subscription>();

  const { createDirectUpload } = useDirectUploads();

  const updateTicketById = async ({ companyId, id, data }: UpdateTicketByIdProps) => {
    try {
      const deparsedData = Ticket.deparseUpdate(data);
      const { image_url: _imageUrl, ...body } = deparsedData;

      const response = await patchV1CompaniesCompanyIdTicketsId({
        path: { 'company-id': companyId, id },
        body,
      });

      const formatted = Ticket.parse(response.data.data);
      ticketsStore.updateTicket(formatted);
      return formatted;
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.tickets.failed_to_update'));
      throw error;
    }
  };

  const updateTicketPotentialDuplicate = useCallback(
    async ({
      companyId,
      id,
      potentialDuplicate,
    }: UpdateTicketPotentialDuplicateProps) => {
      try {
        const response = await patchV1CompaniesCompanyIdTicketsId({
          path: { 'company-id': companyId, id },
          body: { potential_duplicate: potentialDuplicate },
        });

        const formatted = Ticket.parse(response.data.data);
        ticketsStore.updateTicket(formatted);

        return formatted;
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.tickets.failed_to_update'),
        );
        throw error;
      }
    },
    [ticketsStore],
  );

  const deleteTicketImageById = async ({ companyId, id }: DeleteByIdProps) => {
    try {
      await deleteV1CompaniesCompanyIdTicketsTicketIdImage({
        path: { 'company-id': companyId, 'ticket-id': id },
      });
    } catch (error) {
      // A 404 means the image is already gone, so the delete's desired end state
      // is already true — treat it as idempotent success rather than surfacing a
      // noisy error toast (HORIZON-FE-2GQ / TRE-15571).
      const imageAlreadyDeleted = isAxiosError(error) && error.response?.status === 404;

      if (imageAlreadyDeleted) {
        return;
      }

      connection.handleRequestError(
        error,
        t('error_messages.tickets.failed_to_delete_ticket_image'),
      );
    }
  };

  const uploadTicketImageById = async ({
    companyId,
    id,
    file,
  }: UploadTicketImageByIdProps) => {
    try {
      const directUpload = await createDirectUpload(file);
      const response = await postV1CompaniesCompanyIdTicketsTicketIdImage({
        path: { 'company-id': companyId, 'ticket-id': id },
        body: { image: directUpload.signed_id },
      });
      return Ticket.parse(response.data.data);
    } catch (error) {
      // Re-throw a backend image-validation rejection without the generic
      // toast — the drawer shows the reason inline next to the upload widget.
      if (isUploadValidationError(error)) {
        throw error;
      }
      connection.handleRequestError(
        error,
        t('error_messages.tickets.failed_to_upload_ticket_image'),
      );
      throw error;
    }
  };

  const createTicket = async ({ companyId, data }: CreateTicketProps) => {
    try {
      const response = await postV1CompaniesCompanyIdTickets({
        path: { 'company-id': companyId },
        body: data,
      });
      return Ticket.parse(response.data.data);
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.tickets.failed_to_create'));
      throw error;
    }
  };

  const deleteTicketById = async ({ companyId, id }: DeleteByIdProps) => {
    try {
      await deleteV1CompaniesCompanyIdTicketsId({
        path: { 'company-id': companyId, id },
      });
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.tickets.failed_to_delete_ticket'),
      );
    }
  };

  const getTickets = async ({
    companyId,
    cursor,
    filterParams: { from_date, missing_image, missing_load, states, to_date } = {},
    limit,
    link,
    signal,
  }: GetTicketsProps) => {
    try {
      const response = await getV1CompaniesCompanyIdTickets({
        path: { 'company-id': companyId },
        query: {
          'page[limit]': limit,
          ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
          ...(from_date ? { 'filter[from_date]': from_date } : {}),
          ...(to_date ? { 'filter[to_date]': to_date } : {}),
          ...(states ? { 'filter[states]': states } : {}),
          ...(missing_image ? { 'filter[missing_image]': missing_image } : {}),
          ...(missing_load ? { 'filter[missing_load]': missing_load } : {}),
        },
        signal,
      });

      return {
        data: response.data.data.map(Ticket.parse),
        pagination: extractPagination(response),
      };
    } catch (e) {
      if (signal?.aborted) {
        throw e;
      }

      connection.handleRequestError(e, t('error_messages.tickets.failed_to_fetch'));
      throw e;
    }
  };

  const updateState = async ({ companyId, event, id }: UpdateStateProps) => {
    try {
      let response;

      switch (event) {
        case TicketStateEvent.APPROVE:
          response = await putV1CompaniesCompanyIdTicketsTicketIdApprove({
            path: { 'company-id': companyId, 'ticket-id': id },
          });
          break;
        case TicketStateEvent.VOID:
          response = await putV1CompaniesCompanyIdTicketsTicketIdVoid({
            path: { 'company-id': companyId, 'ticket-id': id },
          });
          break;
        default:
          throw new Error(`Unsupported ticket state event: ${event}`);
      }

      const formatted = Ticket.parse(response.data.data);
      ticketsStore.updateTicket(formatted);
      return formatted;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.tickets.failed_to_do_ticket_event', { event }),
      );
      throw error;
    }
  };

  const getAllTicketsByJob = async ({ companyId, jobId }: GetAllTicketsByJobProps) => {
    try {
      return await fetchAllApiPages(
        (query) =>
          getV1CompaniesCompanyIdJobsJobIdTickets({
            path: { 'company-id': companyId, 'job-id': jobId },
            query,
          }),
        Ticket.parse,
      );
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.tickets.failed_to_fetch'));
      throw error;
    }
  };

  const getTicketMetadataById = async ({ companyId, id }: GetTicketMetadataByIdProps) => {
    try {
      const response = await getV1CompaniesCompanyIdTicketsTicketIdAiMetadata({
        path: { 'company-id': companyId, 'ticket-id': id },
      });
      return TicketMetadata.parse(response.data.data);
    } catch (error) {
      // A 404 here means the ticket was deleted out from under an open drawer
      // (e.g. its load was removed in another tab before the RTU pruned the
      // local cache). That is expected, not an actionable error — suppress the
      // toast and Sentry report for it. See HORIZON-FE-2MM.
      connection.handleRequestError(
        error,
        t('error_messages.tickets.failed_to_fetch_ticket_metadata'),
        [404],
      );
      throw error;
    }
  };

  const subscribeToTicketUpdates = useCallback(() => {
    const companyId = getEffectiveUserCompanyId(userStore);
    subscribeToChannel(
      realTimeChannels.TicketUpdateChannel,
      companyId,
      setTicketSubscription,
      (response: { data: Ticket_Read }) => {
        const ticket = Ticket.parse(response?.data);
        const existingTicket = ticketsStore.tickets.find((t) => t.id === ticket.id);
        if (existingTicket) {
          ticketsStore.updateTicket(ticket);
        } else {
          ticketsStore.setTickets([...ticketsStore.tickets, ticket]);
        }
      },
    );
  }, [ticketsStore, userStore]);

  return {
    createTicket,
    deleteTicketImageById,
    deleteTicketById,
    getAllTicketsByJob,
    getTicketMetadataById,
    getTickets,
    updateState,
    updateTicketById,
    updateTicketPotentialDuplicate,
    uploadTicketImageById,
    subscribeToTicketUpdates,
    ticketSubscription,
  } as const;
};
